import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { MdGroups, MdHome, MdFileCopy, MdLeaderboard,MdSchool, MdPlayCircleOutline } from "react-icons/md";
import { FaWallet } from "react-icons/fa";
import { BsGlobe, BsHeadphones } from "react-icons/bs";
import { LuLogOut } from "react-icons/lu";
import { BookOpen, Globe2, Globe2Icon, Landmark, LeafyGreen, MapPinned, NewspaperIcon, ScrollText, Wallet } from "lucide-react";

import Layout from "./Layout";
import { useUser } from "../../../hooks/use-user";

import MenuIcone from "../../../Images/menuicone.svg";
import Logo from "../../../Images/logoIcon.png";
import { IoNotificationsOutline } from "react-icons/io5";
import axios from "../../../axios";
import { toast } from "react-toastify";
import {
  getInstituteLogo,
  getInstituteName,
  getUserInstituteId,
  useInstitute,
} from "../../../hooks/use-institute";

const Sidebar = () => {
  const { data: user } = useUser();
  const userInstituteId = getUserInstituteId(user);
  const { data: institute } = useInstitute(userInstituteId);
  const brandName = getInstituteName(institute) || "Adhyayi";
  const brandLogo = getInstituteLogo(institute) || Logo;
  const isLinkedBrand = Boolean(userInstituteId && institute);

  const navigate = useNavigate();
  const location = useLocation();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [nowOpen, setNowOpen] = useState(false);
  const [states, setStates] = useState([]);

  const [t, i18n] = useTranslation();

  const isContestValid = user?.isContestValid === true;
  const userState = user?.userState || user?.state || user?.stateName || "";
  const hasInstituteAccess = Boolean(userInstituteId && institute);

  // OPEN / CLOSE MENU
  const toggleMenu = () => {
    setIsMenuOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  // DROPDOWN
  const toggleDropdown = () => {
    setNowOpen(!nowOpen);
  };

  // LANGUAGE
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
  };

const getNavbarBg = () => {
  return "bg-transparent";
};

  useEffect(() => {
    const fetchStates = async () => {
      try {
        const res = await axios.get("/all");
        console.log(res, "states");
        setStates(res.data?.data || []);
      } catch (err) {
        toast(err.response?.data?.message || "Failed to fetch states", 3000);
      }
    };

    fetchStates();
  }, []);

  // USER STATE
  const fullStateName = user?.userState || user?.state || user?.stateName || "";

  // FIND SHORT FORM FROM API DATA
  const matchedState = states.find((item) => item.fullName === fullStateName);

  const userStateShort = matchedState?.shortName || fullStateName;

  const hideTopIcons =
    location.pathname.includes("/dash/contest") ||
    location.pathname.includes("/dash/quiz") ||
    location.pathname.includes("/dash/wallet") ||
    location.pathname.includes("/dash/checkout") ||
    location.pathname.includes("/dash/transaction-history") ||
    location.pathname.includes("/dash/profile") ||
    location.pathname.includes("/dash/bank-details") ||
    location.pathname.includes("/dash/security") ||
    location.pathname.includes("/dash/kyc-status") ||
    location.pathname.includes("/dash/dummy-contest") ||
    location.pathname.includes("dash/referral") ||
    location.pathname.includes("dash/leaderBoard")

  const hideWalletIcon =
   location.pathname.includes("/dash/contest")

  return (
    <>
      {/* MENU BUTTON */}
      <div className={`fixed top-0 left-0 w-full z-[70]  ${getNavbarBg()}`}>
        <div className="flex items-center justify-between px-3 py-1">
          {/* LEFT MENU BUTTON */}
          <button
            onClick={toggleMenu}
            className="p-2 text-sm text-gray-500 rounded-full bg-white shadow-lg flex"
          >
            <img src={MenuIcone} className="w-4 h-4" alt="menu" />
          </button>

          {/* RIGHT ICONS */}
          {!hideTopIcons && (
            <div className="flex items-center gap-3">
               {/* LeaderBoard */}
             <Link to="/dash/leaderBoard">
                           <MdLeaderboard size={24}/>
                           </Link>

              {/* WALLET */}
              <Link to="/dash/wallet">
                <button className="relative flex items-center justify-center w-8 h-8 rounded-full bg-white shadow-sm">
                  <Wallet className="text-[#4B2D83] text-lg" />
                </button>
              </Link>

             
            </div>
          )}
          {hideWalletIcon && (
            <Link to="/dash/wallet">
              <button className="relative flex items-center gap-2 py-1 px-3 justify-center rounded-full bg-yellow-100 shadow-sm">
                <Wallet size={12} className="text-[#4B2D83]" />

                <span className="text-sm text-[#4B2D83]">
                  {user?.unifiedWallet?.balance || 0}
                </span>
              </button>
            </Link>
          )}
        </div>
      </div>

      {/* SIDEBAR */}
      <div
        className={`fixed inset-0 z-[80] transition-all duration-300 ${
          isMenuOpen
            ? "translate-x-0 opacity-100"
            : "-translate-x-full opacity-0 pointer-events-none"
        }`}
      >
        {/* OVERLAY */}
        <div className="absolute inset-0 bg-black/40" onClick={closeMenu}></div>

        {/* SIDEBAR CONTENT */}
        <div className="relative w-72 h-full overflow-y-auto bg-[#F4F0FA] shadow-xl p-2 flex flex-col">
          {/* LOGO */}
          <div className="pl-10 pt-0 pb-5">
            <a href="/" className="flex items-center gap-2">
              <img
                src={brandLogo}
                className="w-16 h-16 object-contain rounded-md"
                alt={`${brandName} logo`}
              />
              <span
                className={`max-w-[150px] truncate font-semibold text-[#4B2D83] ${
                  isLinkedBrand ? "text-lg" : "text-xl"
                }`}
                title={brandName}
              >
                {brandName}
              </span>
            </a>
          </div>

          {/* MENU ITEMS */}
          <div className="flex flex-col gap-4 text-xl">
            {[
              {
                name: "Home",
                icon: <MdHome />,
                path: "/dash/home",
              },
             
              {
                name: "Mock Test",
                icon: <BookOpen />,
                path: "/dash/plan",
              },
              ...(hasInstituteAccess
                ? [
                    {
                      name: "Organization Test",
                      icon: <MdSchool />,
                      path: "/dash/my-mock-tests",
                    },
                  ]
                : []),
              //     {
              //   name: "Lectures",
              //   icon: <MdPlayCircleOutline />,
              //   path: "/dash/lectures",
              // },
              {
                name: "Contest",
                icon: <MdFileCopy />,
                path: "/dash/contest",
              },
              {
                name: "Leader Board",
                icon: <MdLeaderboard />,
                path: "/dash/leaderBoard",
              },
            
              {
                name: "Current Affairs",
                icon: <ScrollText />,
                path: "/dash/current-affairs",
              },
                {
                name: userStateShort ? `${userStateShort} GK` : "State GK",
                subtitle: "",
                icon: <Landmark />,
                path: "/dash/state-gk",
              },
              {
                name: "Wallet",
                icon: <FaWallet />,
                path: "/dash/wallet",
              },
               {
                name: "Refer & Get Credits",
                icon: <MdGroups />,
                path: "/dash/referral",
              },
              {
                name: "Language",
                icon: <BsGlobe />,
                action: toggleDropdown,
              },
             
              {
                name: "Support",
                icon: <BsHeadphones />,
                path: "/dash/support",
              },
             
            ].map((item, i) => {
              const isActive = location.pathname === item.path;

              return (
                <div
                  key={i}
                  onClick={() => {
                    if (item.path) {
                      navigate(item.path);
                      closeMenu();
                    } else if (item.action) {
                      item.action();
                    }
                  }}
                  className={`flex items-center pl-10 gap-4 rounded-full cursor-pointer transition-all duration-200
                  
                  ${
                    isActive
                      ? "bg-white shadow text-[#F5A623] font-semibold py-2 px-3"
                      : "text-[#4B2D83] hover:bg-white/70 py-2 px-3"
                  }`}
                >
                  <div className="text-xl">{item.icon}</div>

                  <span className="leading-tight">
                    {item.name}
                    {item.subtitle && (
                      <span className="block text-xs font-medium text-[#7B6A92]">
                        {item.subtitle}
                      </span>
                    )}
                  </span>
                </div>
              );
            })}

            {/* LOGOUT */}
            <div
              onClick={() => {
                localStorage.removeItem("token");
                navigate("/login");
              }}
              className="flex items-center pl-10 gap-4 px-3 py-3 rounded-xl cursor-pointer text-[#4B2D83] hover:bg-white mt-0"
            >
              <LuLogOut />

              <span>Logout</span>
            </div>
          </div>
        </div>
      </div>

      {/* DESKTOP SIDEBAR */}
      <div className="hidden sm:flex fixed left-0 top-20 z-[60] h-[calc(100dvh-5rem)] overflow-y-auto w-64 bg-[#F4F0FA] shadow-xl flex-col p-2">
        {/* MENU */}
        <div className="flex flex-col gap-2 text-lg mt-2">
          {[
            {
              name: "Home",
              icon: <MdHome />,
              path: "/dash/home",
            },
          
            {
              name: "Mock Test",
              icon: <BookOpen />,
              path: "/dash/plan",
            },
            ...(hasInstituteAccess
              ? [
                  {
                    name: "Organization Test",
                    icon: <MdSchool />,
                    path: "/dash/my-mock-tests",
                  },
                ]
              : []),
            //      {
            //   name: "Lectures",
            //   icon: <MdPlayCircleOutline />,
            //   path: "/dash/lectures",
            // },
            {
              name: "Contest",
              icon: <MdFileCopy />,
              path: "/dash/contest",
            },
               
            {
                name: "Leader Board",
                icon: <MdLeaderboard />,
                path: "/dash/leaderBoard",
              },
          
            {
              name: "Current Affairs",
              icon: <ScrollText />,
              path: "/dash/current-affairs",
            },
            {
              name: userStateShort ? `${userStateShort} GK` : "State GK",
              subtitle: "",
              icon: <Landmark />,
              path: "/dash/state-gk",
            },
              {
              name: "Wallet",
              icon: <FaWallet />,
              path: "/dash/wallet",
            },
             {
              name: "Refer & Get Credits",
              icon: <MdGroups />,
              path: "/dash/referral",
            },
             {
                name: "Language",
                icon: <BsGlobe />,
                action: toggleDropdown,
              },
             
            {
              name: "Support",
              icon: <BsHeadphones />,
              path: "/dash/support",
            },
           
          ].map((item, i) => {
            const isActive = location.pathname === item.path;

            return (
              <div
                key={i}
                onClick={() => navigate(item.path)}
                className={`flex items-center gap-2 rounded-full cursor-pointer transition-all duration-200 px-5 py-3
          
          ${
            isActive
              ? "bg-white shadow text-[#F5A623] font-semibold"
              : "text-[#4B2D83] hover:bg-white/70"
          }`}
              >
                <div className="text-xl">{item.icon}</div>

                <span className="leading-tight">
                  {item.name}
                  {item.subtitle && (
                    <span className="block text-xs font-medium text-[#7B6A92]">
                      {item.subtitle}
                    </span>
                  )}
                </span>
              </div>
            );
          })}

          {/* LOGOUT */}
          <div
            onClick={() => {
              localStorage.removeItem("token");
              navigate("/login");
            }}
            className="flex items-center gap-4 px-5 py-3 rounded-full cursor-pointer text-[#4B2D83] hover:bg-white/70 mt-0"
          >
            <LuLogOut />
            <span>Logout</span>
          </div>
        </div>
      </div>

      {/* PAGE CONTENT */}
      <div className="sm:ml-64 pt-10 sm:pt-0 lg:py-8 sm:h-screen">
        <Layout />
      </div>
    </>
  );
};

export default Sidebar;
